import { useRef } from 'react';

import {
  FacebookShareButton,
  TwitterShareButton,
  EmailShareButton,
  WhatsappShareButton
} from 'react-share';

import {
  CardDetailsType,
  ReferralContentItemType
} from 'src/components/thumbnail/types';
import { useAppStore } from 'src/hooks/useAppStore';
import { getOtherIcons } from 'src/styles/font';
import { mixpanelObject } from 'src/utils/analytics';
import {
  contentPropertySetter,
  ctaEvent,
  shareEvent
} from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { getDeviceType } from 'src/utils/CheckDeviceType';
import { ContentItemType } from 'src/utils/commonTypes';
import { DEVICE_TYPES } from 'src/utils/constants/AppConstants';
import { OtherIconsSet } from 'src/utils/constants/IconsConstants';
import {
  LOGIN_REGISTER_TEXT,
  Share_Constant
} from 'src/utils/constants/stringConstants';
import { WINDOW_LISTNER_EVENTS } from 'src/utils/GlobalListners/WindowListnerConstants';
import useWindowEvent from 'src/utils/GlobalListners/WindowListnerHook';

import {
  container,
  copyLink,
  mediaBgStyle,
  shareModelContainer,
  shareWrapper
} from './ShareModalStyle';
import {
  MIXPANEL_SHARING_PLATFORM,
  MIXPANEL_FACEBOOK,
  MIXPANEL_TWITTER,
  MIXPANEL_EMAIL,
  MIXPANEL_WHATSAPP,
  MIXPANEL_COPY_LINK
} from '../../../../utils/analytics/mixpanel/mixpanelConstants';

type ShareModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  item?: ContentItemType;
  shareURL: string;
  toastMsgHandler: () => void;
  cardDetails?: CardDetailsType;
  cssClassName?: string;
  referralContentItem?: ReferralContentItemType;
  isFeatureReferral?: boolean;
  /**
   * This component is normally used for content sharing, but that will pick up a template from translation.
   * Hence, using this prop in case we need to share some other features too (ex. quiz, etc.)
   */
  overrideShareText?: string;
};

const ShareModal = (props: ShareModalProps) => {
  const {
    item,
    shareURL,
    handleClose,
    toastMsgHandler,
    cardDetails,
    cssClassName = '',
    referralContentItem,
    isFeatureReferral,
    overrideShareText
  } = props;
  const title =
    isFeatureReferral && referralContentItem?.title
      ? referralContentItem.title
      : item?.title;
  const copyLinkText =
    isFeatureReferral && referralContentItem?.title
      ? `${referralContentItem.title} ${referralContentItem.referralDeepLink}\n\n`
      : shareURL;
  const userAgent = navigator.userAgent;
  const deviceType = getDeviceType(userAgent);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { translations } = useAppStore();
  const shareText =
    overrideShareText ||
    `${translations?.consumption?.SHARE_TEXT?.replace('{{content}}', title)} `;

  const copyTextLinks = isFeatureReferral
    ? copyLinkText
    : `${shareText} ${shareURL}`;

  const copyToClipBoard = (url: string) => {
    navigator?.clipboard?.writeText(url);
    toastMsgHandler();
    trackSocialMediaSharingMixpanelEvent(Share_Constant.COPY_LINK);
  };

  const handleClickOutside = (event: Event) => {
    if (!wrapperRef?.current?.contains(event.target as Node)) {
      handleClose();
    }
  };

  useWindowEvent(WINDOW_LISTNER_EVENTS.MOUSEDOWN, handleClickOutside);

  const trackSocialMediaSharingMixpanelEvent = (platformName: string) => {
    handleClose();
    switch (platformName) {
      case LOGIN_REGISTER_TEXT.FACEBOOK:
        mixpanelObject.setProperty(
          MIXPANEL_SHARING_PLATFORM,
          MIXPANEL_FACEBOOK
        );
        break;
      case LOGIN_REGISTER_TEXT.TWITTER:
        mixpanelObject.setProperty(MIXPANEL_SHARING_PLATFORM, MIXPANEL_TWITTER);
        break;
      case LOGIN_REGISTER_TEXT.CONTINUE_WITH_EMAIL:
        mixpanelObject.setProperty(MIXPANEL_SHARING_PLATFORM, MIXPANEL_EMAIL);
        break;
      case LOGIN_REGISTER_TEXT.WHATSAPP:
        mixpanelObject.setProperty(
          MIXPANEL_SHARING_PLATFORM,
          MIXPANEL_WHATSAPP
        );
        break;
      case Share_Constant.COPY_LINK:
        mixpanelObject.setProperty(
          MIXPANEL_SHARING_PLATFORM,
          MIXPANEL_COPY_LINK
        );
        break;
      default:
    }
    contentPropertySetter(mixpanelObject, item, cardDetails);
    shareEvent();
    ctaEvent();
  };

  return (
    <div
      data-testid="share-model-container"
      css={shareModelContainer(cssClassName)}
      ref={wrapperRef}>
      <div css={container}>
        <div
          data-testid="share-modal-wrapper"
          css={shareWrapper}
          onClick={(e) => e.stopPropagation()}>
          <FacebookShareButton
            data-testid="facebook-share-btn"
            quote={shareText}
            onClick={() =>
              trackSocialMediaSharingMixpanelEvent(LOGIN_REGISTER_TEXT.FACEBOOK)
            }
            url={shareURL}>
            <span css={[getOtherIcons(OtherIconsSet.FACEBOOK), mediaBgStyle]} />
          </FacebookShareButton>
          <TwitterShareButton
            data-testid="twitter-share-btn"
            title={shareText}
            onClick={() =>
              trackSocialMediaSharingMixpanelEvent(LOGIN_REGISTER_TEXT.TWITTER)
            }
            url={shareURL}>
            <span css={[getOtherIcons(OtherIconsSet.TWITTER), mediaBgStyle]} />
          </TwitterShareButton>
          <EmailShareButton
            data-testid="email-share-btn"
            body={shareText}
            url={shareURL}
            onClick={() =>
              trackSocialMediaSharingMixpanelEvent(
                LOGIN_REGISTER_TEXT.CONTINUE_WITH_EMAIL
              )
            }
            openShareDialogOnClick={true}>
            <span css={[getOtherIcons(OtherIconsSet.MAIL), mediaBgStyle]} />
          </EmailShareButton>
          {deviceType === DEVICE_TYPES.MOBILE ? (
            <WhatsappShareButton
              data-testid="whatsapp-share-btn"
              title={shareText}
              onClick={() =>
                trackSocialMediaSharingMixpanelEvent(
                  LOGIN_REGISTER_TEXT.WHATSAPP
                )
              }
              url={shareURL}>
              <span
                css={[getOtherIcons(OtherIconsSet.WHATSAPP), mediaBgStyle]}
              />
            </WhatsappShareButton>
          ) : null}
          <div
            data-testid="copy-link"
            css={copyLink}
            onClick={() => copyToClipBoard(copyTextLinks)}></div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
